import React from "react";
import { Button, Dropdown, Modal, Typography } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useFetcher, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { EnsureTransactionReceiptResult } from "~/types/paymentReceipt";
import { INTENTS, ROUTES } from "~/utils";

export type GetReceiptButtonProps = {
  transactionID: string;
};

type ReceiptActionFailure = {
  success: false;
  error: string;
};

const RECEIPT_LANGUAGE_OPTIONS = [
  { key: "eng", label: "English" },
  { key: "fr", label: "Français" },
  { key: "es", label: "Español" },
] as const;

function normalizeUiLanguage(lng: string | undefined): string {
  const code = (lng ?? "eng").split("-")[0];
  if (code === "eng" || code === "fr" || code === "es") return code;
  return "eng";
}

function isReceiptActionFailure(
  data: unknown,
): data is ReceiptActionFailure {
  return (
    typeof data === "object" &&
    data != null &&
    "success" in data &&
    (data as ReceiptActionFailure).success === false
  );
}

export function GetReceiptButton({ transactionID }: GetReceiptButtonProps) {
  const { t, i18n } = useTranslation();
  const fetcher = useFetcher({ key: `transaction-receipt-${transactionID}` });
  const [result, setResult] = React.useState<EnsureTransactionReceiptResult | null>(
    null,
  );
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState(() =>
    normalizeUiLanguage(i18n.language),
  );
  const lastHandledKeyRef = React.useRef<string | null>(null);

  const isLoading = fetcher.state !== "idle";

  const handleEnsureReceipt = React.useCallback(
    (language: string) => {
      const normalized = normalizeUiLanguage(language);
      setSelectedLanguage(normalized);
      fetcher.submit(
        {
          intent: INTENTS._ensure_transaction_receipt,
          transactionID,
          language: normalized,
        },
        { method: "POST" },
      );
    },
    [fetcher, transactionID],
  );

  React.useEffect(() => {
    if (fetcher.state !== "idle" || fetcher.data == null) {
      return;
    }

    const actionKey = `${transactionID}:${fetcher.state}:${JSON.stringify(fetcher.data)}`;
    if (lastHandledKeyRef.current === actionKey) {
      return;
    }
    lastHandledKeyRef.current = actionKey;

    if (isReceiptActionFailure(fetcher.data)) {
      Modal.error({
        title: t("Receipt_error"),
        content: fetcher.data.error ?? t("Something_went_wrong_Please_try_again"),
      });
      return;
    }

    const data = fetcher.data as EnsureTransactionReceiptResult;
    setResult(data);

    if (data.status === "Ready" && data.downloadUrl) {
      window.open(data.downloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (
      data.status === "Pending" ||
      data.status === "Failed" ||
      data.status === "Error"
    ) {
      setModalOpen(true);
    }
  }, [fetcher.data, fetcher.state, t, transactionID]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setResult(null);
  };

  const showAccountLink =
    result?.failureCode === "MISSING_SIGNATURE" ||
    result?.failureReason === "MISSING_SIGNATURE";

  return (
    <>
      <Dropdown.Button
        type="link"
        size="small"
        loading={isLoading}
        onClick={() => handleEnsureReceipt(normalizeUiLanguage(i18n.language))}
        icon={<DownOutlined />}
        menu={{
          items: RECEIPT_LANGUAGE_OPTIONS.map((opt) => ({
            key: opt.key,
            label: opt.label,
          })),
          onClick: ({ key }) => handleEnsureReceipt(String(key)),
        }}
      >
        {t("Get_receipt")}
      </Dropdown.Button>

      <Modal
        open={modalOpen}
        onCancel={handleCloseModal}
        title={
          result?.status === "Pending"
            ? t("Receipt_preparing")
            : t("Receipt_error")
        }
        footer={
          result?.status === "Pending"
            ? [
                <Button key="close" onClick={handleCloseModal}>
                  {t("Close")}
                </Button>,
                <Button
                  key="refresh"
                  type="primary"
                  loading={isLoading}
                  onClick={() => handleEnsureReceipt(selectedLanguage)}
                >
                  {t("Refresh")}
                </Button>,
              ]
            : [
                <Button key="close" onClick={handleCloseModal}>
                  {t("Close")}
                </Button>,
                ...(result?.retryable
                  ? [
                      <Button
                        key="retry"
                        type="primary"
                        loading={isLoading}
                        onClick={() => handleEnsureReceipt(selectedLanguage)}
                      >
                        {t("Try_again")}
                      </Button>,
                    ]
                  : []),
              ]
        }
      >
        {result?.status === "Pending" ? (
          <Typography.Paragraph>{t("Receipt_preparing_help")}</Typography.Paragraph>
        ) : (
          <>
            {result?.failureMessage ? (
              <Typography.Paragraph>{result.failureMessage}</Typography.Paragraph>
            ) : null}
            {result?.failureAction ? (
              <Typography.Paragraph type="secondary">
                {result.failureAction}
              </Typography.Paragraph>
            ) : null}
            {showAccountLink ? (
              <Typography.Paragraph>
                <Link to={ROUTES.account} onClick={handleCloseModal}>
                  {t("Go_to_account_settings")}
                </Link>
              </Typography.Paragraph>
            ) : null}
          </>
        )}
      </Modal>
    </>
  );
}
